import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        await axios.post(`${API}/testimonials/seed`).catch(() => {});
        const { data } = await axios.get(`${API}/testimonials`, { params: { approved_only: true } });
        setItems(data || []);
      } catch {}
    })();
  }, []);

  if (!items.length) return null;
  const cur = items[idx % items.length];

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section data-testid="testimonials-section" className="bg-ink text-cream py-24 md:py-32 px-5 md:px-10 lg:px-16 relative overflow-hidden">
      <div aria-hidden className="absolute -top-20 right-0 w-[400px] h-[400px] rounded-full bg-lime/10 blur-[120px]" />
      <div className="relative z-10 grid grid-cols-12 gap-6 mb-10 items-end">
        <div className="col-span-12 md:col-span-8">
          <div className="inline-flex items-center gap-2 bg-lime/10 border border-lime/30 rounded-full px-4 py-2 mb-6">
            <span className="font-body text-xs tracking-widest uppercase text-lime">Depoimentos</span>
          </div>
          <h2 className="font-display font-extrabold text-5xl md:text-7xl leading-[0.92]">
            <span className="font-hand text-6xl md:text-8xl text-hot">"</span>
            quem já passou<br/>por cá, <span className="text-lime">diz.</span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-4 flex md:justify-end gap-3">
          <button
            data-testid="testimonial-prev-button"
            onClick={prev}
            className="w-12 h-12 rounded-full border-2 border-cream/20 flex items-center justify-center hover:bg-lime hover:text-ink hover:border-lime transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="testimonial-next-button"
            onClick={next}
            className="w-12 h-12 rounded-full border-2 border-cream/20 flex items-center justify-center hover:bg-lime hover:text-ink hover:border-lime transition-colors"
            aria-label="Seguinte"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="relative z-10 min-h-[260px]">
        <Quote size={80} className="text-hot/30 absolute -top-6 -left-2" />
        <AnimatePresence mode="wait">
          <motion.blockquote
            key={cur.id}
            data-testid="testimonial-quote"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.45 }}
            className="relative font-display font-medium text-3xl md:text-5xl leading-snug text-cream max-w-5xl"
          >
            {cur.message}
            <footer className="mt-8 font-body text-sm uppercase tracking-widest text-lime">
              — {cur.name} {cur.role ? `· ${cur.role}` : ""}
            </footer>
          </motion.blockquote>
        </AnimatePresence>
      </div>

      <div className="relative z-10 mt-10 flex gap-2">
        {items.map((_, i) => (
          <span
            key={i}
            data-testid={`testimonial-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-lime" : "w-5 bg-cream/20"}`}
          />
        ))}
      </div>
    </section>
  );
}
